import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
    Box, Card, Sheet, Stack, Typography, Button, Input, Select, Option,
    IconButton, Tooltip, Chip, CircularProgress, LinearProgress, Switch, Grid,
    Modal, ModalDialog, ModalClose, ModalOverflow, Divider, Avatar,
} from "@mui/joy";
import {
    Area, AreaChart, Bar, BarChart, CartesianGrid,
    Legend, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router";

import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import InventoryRoundedIcon from "@mui/icons-material/Inventory2Rounded";
import UnarchiveRoundedIcon from "@mui/icons-material/UnarchiveRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";

import { LayoutContext } from "../../context";
import {
    APIGetClientPaymentsStats,
    APIGetClientPayments,
    APIArchiveClientPayment,
    APISyncClientPayment,
    APIMarkPaidClientPayment,
    APIRetryClientPayment,
} from "../../api";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

// ── Утилиты ──────────────────────────────────────────────
const fmtMoney = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("ru-RU", { maximumFractionDigits: 0 }) + " ₽");
const fmtNum = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("ru-RU"));
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const fmtDate = (s) => (s ? new Date(s).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const PRESETS = [
    { label: "7 дней", from: () => daysAgo(6), to: todayISO },
    { label: "30 дней", from: () => daysAgo(29), to: todayISO },
    { label: "90 дней", from: () => daysAgo(89), to: todayISO },
];

// Статусы оплаты → цвета (label берём из status_display сервера)
const PAY_STATUS = {
    created:  { color: "primary", accent: "#572AC3", bgSoft: "rgba(87,42,195,0.06)" },
    pending:  { color: "warning", accent: "#f59e0b", bgSoft: "rgba(245,158,11,0.06)" },
    paid:     { color: "success", accent: "#16a34a", bgSoft: "rgba(22,163,74,0.06)" },
    failed:   { color: "danger",  accent: "#dc2626", bgSoft: "rgba(220,38,38,0.06)" },
    expired:  { color: "neutral", accent: "#9ca3af", bgSoft: "rgba(107,114,128,0.08)" },
    refunded: { color: "neutral", accent: "#8b5cf6", bgSoft: "rgba(139,92,246,0.08)" },
    canceled: { color: "danger",  accent: "#9ca3af", bgSoft: "rgba(107,114,128,0.08)" },
};
const STATUS_FILTER_OPTIONS = [
    { value: "all", label: "Все статусы" },
    { value: "created", label: "Создан / ожидает оплаты" },
    { value: "pending", label: "В обработке" },
    { value: "paid", label: "Оплачен" },
    { value: "failed", label: "Ошибка оплаты" },
    { value: "expired", label: "Истёк срок ссылки" },
    { value: "refunded", label: "Возврат" },
    { value: "canceled", label: "Отменён" },
];

// ── Карточка метрики (как в Статистике) ──────────────────
function MetricCard({ title, value, value2, sub, color = "#6366f1", icon }) {
    return (
        <Card sx={{ flex: "1 1 170px", minWidth: 150, borderRadius: "18px", border: "1.5px solid", borderColor: `${color}22`,
            background: `linear-gradient(135deg, ${color}08 0%, ${color}03 100%)`, boxShadow: "none", p: 2, gap: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Box sx={{ color, display: "flex" }}>{icon}</Box>
                <Typography level="body-xs" sx={{ fontWeight: 600, color: "text.secondary" }}>{title}</Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color, lineHeight: 1.1 }}>{value}</Typography>
                {value2 !== undefined && (
                    <>
                        <Typography sx={{ fontSize: "1.1rem", fontWeight: 400, color: `${color}88` }}>/</Typography>
                        <Typography sx={{ fontSize: "1.3rem", fontWeight: 700, color: `${color}aa` }}>{value2}</Typography>
                    </>
                )}
            </Stack>
            {sub && <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.25 }}>{sub}</Typography>}
        </Card>
    );
}

function ChartTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ background: "rgba(255,255,255,0.97)", border: "1px solid #e0e7ff", borderRadius: "12px", p: 1.5, boxShadow: "0 8px 24px rgba(99,102,241,0.12)", minWidth: 150 }}>
            <Typography level="body-xs" sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Typography>
            {payload.map((p, i) => (
                <Stack key={i} direction="row" alignItems="center" spacing={0.75}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color }} />
                    <Typography level="body-xs" sx={{ color: "text.secondary" }}>{p.name}:</Typography>
                    <Typography level="body-xs" sx={{ fontWeight: 700 }}>{p.name?.includes("₽") ? fmtMoney(p.value) : fmtNum(p.value)}</Typography>
                </Stack>
            ))}
        </Box>
    );
}

// ── Панель статистики ────────────────────────────────────
function PaymentStatsPanel({ token, dateFrom, dateTo, onPreset, setDateFrom, setDateTo }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const data = await APIGetClientPaymentsStats(token, { date_from: dateFrom, date_to: dateTo });
            setStats(data);
        } finally { setLoading(false); }
    }, [token, dateFrom, dateTo]);

    useEffect(() => { load(); }, [load]);

    const t = stats?.totals || {};
    const countChart = useMemo(() => {
        if (!stats?.labels) return [];
        return stats.labels.map((lab, i) => ({ label: lab, "Создано": stats.created?.[i] || 0, "Оплачено": stats.paid?.[i] || 0 }));
    }, [stats]);
    const moneyChart = useMemo(() => {
        if (!stats?.labels) return [];
        return stats.labels.map((lab, i) => ({ label: lab, "Приход ₽": stats.income?.[i] || 0 }));
    }, [stats]);

    return (
        <>
            {/* Период */}
            <Card sx={{ mb: 2.5, borderRadius: "18px", border: "1.5px solid #e0e7ff", p: 2, background: "linear-gradient(135deg, #f8faff, #eef2ff)" }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} flexWrap="wrap" useFlexGap>
                    <CalendarMonthRoundedIcon sx={{ color: "#6366f1", flexShrink: 0 }} />
                    <Typography level="body-sm" sx={{ fontWeight: 700, flexShrink: 0 }}>Период:</Typography>
                    <Stack direction="row" spacing={0.75}>
                        {PRESETS.map(p => {
                            const active = dateFrom === p.from() && dateTo === p.to();
                            return (
                                <Box key={p.label} onClick={() => onPreset(p)}
                                    sx={{ px: 1.5, py: 0.5, borderRadius: "20px", cursor: "pointer", border: "1.5px solid",
                                        borderColor: active ? "#6366f1" : "rgba(99,102,241,0.2)", bgcolor: active ? "#6366f1" : "white",
                                        color: active ? "white" : "#6366f1", fontSize: "0.75rem", fontWeight: 700, transition: "all .15s" }}>
                                    {p.label}
                                </Box>
                            );
                        })}
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: { sm: "auto" } }}>
                        <Typography level="body-xs" sx={{ color: "text.secondary" }}>с</Typography>
                        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                            style={{ border: "1.5px solid rgba(99,102,241,0.25)", borderRadius: 10, padding: "4px 10px", fontSize: "0.82rem", outline: "none", fontFamily: "inherit" }} />
                        <Typography level="body-xs" sx={{ color: "text.secondary" }}>по</Typography>
                        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                            style={{ border: "1.5px solid rgba(99,102,241,0.25)", borderRadius: 10, padding: "4px 10px", fontSize: "0.82rem", outline: "none", fontFamily: "inherit" }} />
                    </Stack>
                </Stack>
            </Card>

            {loading && <LinearProgress color="primary" sx={{ mb: 2 }} />}

            {/* Метрики */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2.5 }}>
                <MetricCard title="Всего платежей" value={fmtNum(t.total)} color="#6366f1" icon={<PaymentsRoundedIcon sx={{ fontSize: 18 }} />} />
                <MetricCard title="Оплачено" value={fmtNum(t.paid_count)} sub={fmtMoney(t.paid_sum)} color="#16a34a" icon={<CheckCircleRoundedIcon sx={{ fontSize: 18 }} />} />
                <MetricCard title="В ожидании" value={fmtNum(t.pending)} color="#f59e0b" icon={<HourglassEmptyRoundedIcon sx={{ fontSize: 18 }} />} />
                <MetricCard title="Ошибки / отмены" value={fmtNum(t.failed)} value2={fmtNum(t.refunded)} sub="ошибок / возвратов" color="#dc2626" icon={<ErrorOutlineRoundedIcon sx={{ fontSize: 18 }} />} />
                <MetricCard title="Приход" value={fmtMoney(t.paid_sum)} sub={`Средний чек: ${fmtMoney(t.avg_check)}`} color="#059669" icon={<TrendingUpRoundedIcon sx={{ fontSize: 18 }} />} />
                <MetricCard title="Конверсия оплат" value={`${fmtNum(t.conversion)}%`} color="#0ea5e9" icon={<PercentRoundedIcon sx={{ fontSize: 18 }} />} />
            </Box>

            {/* График: создано/оплачено по дням */}
            <Card sx={{ mb: 2.5, borderRadius: "20px", border: "1.5px solid #e0e7ff", p: { xs: 2, md: 3 } }}>
                <Typography level="title-sm" sx={{ fontWeight: 700, mb: 2 }}>Платежи по дням</Typography>
                {countChart.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={countChart} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                            <defs>
                                <linearGradient id="grad-created" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#6366f1" stopOpacity={0.18} /><stop offset="95%" stopColor="#6366f1" stopOpacity={0.02} /></linearGradient>
                                <linearGradient id="grad-paid" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#16a34a" stopOpacity={0.18} /><stop offset="95%" stopColor="#16a34a" stopOpacity={0.02} /></linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                            <RTooltip content={<ChartTooltip />} />
                            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                            <Area type="monotone" dataKey="Создано" stroke="#6366f1" fill="url(#grad-created)" strokeWidth={2.5} dot={{ r: 3, fill: "#6366f1", strokeWidth: 0 }} activeDot={{ r: 5 }} />
                            <Area type="monotone" dataKey="Оплачено" stroke="#16a34a" fill="url(#grad-paid)" strokeWidth={2.5} dot={{ r: 3, fill: "#16a34a", strokeWidth: 0 }} activeDot={{ r: 5 }} />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <Box sx={{ py: 6, textAlign: "center" }}><Typography level="body-sm" sx={{ color: "text.tertiary" }}>Нет данных за период</Typography></Box>
                )}
            </Card>

            {/* График: приход по дням */}
            <Card sx={{ mb: 2.5, borderRadius: "20px", border: "1.5px solid #e0e7ff", p: { xs: 2, md: 3 } }}>
                <Typography level="title-sm" sx={{ fontWeight: 700, mb: 2 }}>Приход по дням (₽)</Typography>
                {moneyChart.some(d => d["Приход ₽"] > 0) ? (
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={moneyChart} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                            <RTooltip content={<ChartTooltip />} />
                            <Bar dataKey="Приход ₽" fill="#059669" radius={[4, 4, 0, 0]} maxBarSize={32} />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Box sx={{ py: 4, textAlign: "center" }}><Typography level="body-sm" sx={{ color: "text.tertiary" }}>Оплат за период нет</Typography></Box>
                )}
            </Card>
        </>
    );
}

// ── Модалка деталей платежа ──────────────────────────────
function PaymentDetailModal({ open, payment, onClose }) {
    if (!payment) return null;
    const cfg = PAY_STATUS[payment.status] || PAY_STATUS.created;
    const Row = ({ label, value }) => (
        <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>{label}</Typography>
            <Typography level="body-sm" sx={{ fontWeight: 600, textAlign: "right" }}>{value || "—"}</Typography>
        </Stack>
    );
    return (
        <Modal open={open} onClose={onClose} sx={{ zIndex: 10200 }}>
            <ModalOverflow>
                <ModalDialog sx={{ width: { xs: "100%", sm: 480 }, borderRadius: "xl" }}>
                    <ModalClose />
                    <Typography level="title-md" sx={{ fontWeight: 700, mb: 0.5 }}>Платёж #{payment.id}</Typography>
                    <Chip size="sm" variant="soft" sx={{ alignSelf: "flex-start", color: cfg.accent, bgcolor: cfg.bgSoft, mb: 1.5 }}>
                        {payment.status_display}
                    </Chip>
                    <Sheet variant="soft" sx={{ borderRadius: "lg", p: 1.5 }}>
                        <Row label="Сумма" value={fmtMoney(payment.amount)} />
                        <Row label="Назначение" value={payment.purpose} />
                        <Row label="Сделка" value={payment.deal_id ? `#${payment.deal_id} · ${fmtMoney(payment.deal_opportunity)}` : "—"} />
                        <Row label="Заявка" value={payment.application_id ? `#${payment.application_id}` : "—"} />
                        <Row label="Событие" value={payment.event_id ? `#${payment.event_id}` : "—"} />
                        <Row label="Клиент" value={payment.client_name} />
                        <Row label="Телефон" value={payment.client_phone} />
                        <Row label="Адрес" value={payment.address} />
                        <Row label="Город" value={payment.city} />
                        <Divider sx={{ my: 0.5 }} />
                        <Row label="Создан" value={fmtDate(payment.date_create)} />
                        <Row label="Оплачен" value={fmtDate(payment.paid_at)} />
                        <Row label="Действует до" value={fmtDate(payment.expires_at)} />
                        <Row label="Провайдер" value={payment.provider_display} />
                        <Row label="Способ" value={payment.payment_mode} />
                        <Row label="operationId" value={payment.operation_id} />
                        {payment.attempts > 1 && <Row label="Попыток создания" value={String(payment.attempts)} />}
                        {payment.error_code && <Row label="Код ошибки" value={payment.error_code} />}
                    </Sheet>
                    {payment.error_message && (
                        <Sheet variant="soft" color="danger" sx={{ borderRadius: "lg", p: 1.5, mt: 1.5 }}>
                            <Typography level="body-xs" sx={{ fontWeight: 700, mb: 0.5 }}>Причина отказа</Typography>
                            <Typography level="body-xs" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {payment.error_message}
                            </Typography>
                        </Sheet>
                    )}
                    {payment.is_payable && payment.payment_url && (
                        <Button component="a" href={payment.payment_url} target="_blank" rel="noopener"
                            startDecorator={<OpenInNewRoundedIcon />} variant="soft" color="primary" sx={{ mt: 1.5, borderRadius: "lg" }}>
                            Открыть ссылку оплаты
                        </Button>
                    )}
                </ModalDialog>
            </ModalOverflow>
        </Modal>
    );
}

// ── Карточка платежа (стиль как у заявок) ────────────────
function PaymentCard({ payment, onOpen, onArchive, onSync, onApprove, onRetry, navigate, busy }) {
    const cfg = PAY_STATUS[payment.status] || PAY_STATUS.created;
    return (
        <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}>
            <Sheet variant="outlined" sx={{ borderRadius: "xl", overflow: "hidden", position: "relative",
                bgcolor: payment.is_archived ? "neutral.100" : "background.surface", borderColor: "divider",
                transition: "all .2s ease", "&:hover": { boxShadow: "md" } }}>
                <Box sx={{ position: "absolute", top: 0, left: 0, bottom: 0, width: 4, bgcolor: cfg.accent }} />
                <Box sx={{ p: 2, pl: 2.5 }}>
                    {/* Шапка */}
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
                        <Chip size="sm" variant="soft" sx={{ color: cfg.accent, bgcolor: cfg.bgSoft, fontWeight: 700 }}>#{payment.id}</Chip>
                        <Chip size="sm" variant="soft" sx={{ color: cfg.accent, bgcolor: cfg.bgSoft }}>{payment.status_display}</Chip>
                        {payment.is_archived && <Chip size="sm" color="neutral" variant="soft" startDecorator={<InventoryRoundedIcon sx={{ fontSize: 13 }} />}>Архив</Chip>}
                        <Box sx={{ ml: "auto" }}>
                            <Typography level="title-md" sx={{ fontWeight: 800, color: cfg.accent }}>{fmtMoney(payment.amount)}</Typography>
                        </Box>
                    </Stack>

                    {/* Сделка / клиент */}
                    {payment.deal_id && (
                        <Chip size="sm" variant="soft" color="neutral" sx={{ mb: 0.75 }}>
                            Сделка #{payment.deal_id}{payment.deal_opportunity ? ` · ${fmtMoney(payment.deal_opportunity)}` : ""}
                        </Chip>
                    )}
                    {payment.client_name && (
                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>{payment.client_name}</Typography>
                    )}
                    {payment.client_phone && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "text.secondary", mt: 0.25 }}>
                            <PhoneRoundedIcon sx={{ fontSize: 14 }} /><Typography level="body-xs">{payment.client_phone}</Typography>
                        </Stack>
                    )}
                    {payment.address && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "text.secondary", mt: 0.25 }}>
                            <LocationOnRoundedIcon sx={{ fontSize: 14 }} /><Typography level="body-xs" noWrap>{payment.address}</Typography>
                        </Stack>
                    )}

                    <Stack direction="row" spacing={1} sx={{ mt: 0.75, color: "text.tertiary" }}>
                        <Typography level="body-xs">Создан: {fmtDate(payment.date_create)}</Typography>
                        {payment.paid_at && <Typography level="body-xs">· Оплачен: {fmtDate(payment.paid_at)}</Typography>}
                        {payment.attempts > 1 && <Typography level="body-xs">· Попыток: {payment.attempts}</Typography>}
                    </Stack>

                    {/* Причина отказа. Раньше на карточке был только красный бейдж
                        «Ошибка оплаты» — почему не прошло, узнать было негде. */}
                    {payment.error_message && (
                        <Sheet variant="soft" color="danger" sx={{ borderRadius: "lg", p: 1, mt: 1 }}>
                            <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                <WarningAmberRoundedIcon sx={{ fontSize: 15, mt: "2px" }} />
                                <Box sx={{ minWidth: 0 }}>
                                    <Box component="details">
                                        <Typography component="summary" level="body-xs"
                                            sx={{ cursor: "pointer", fontWeight: 600 }}>
                                            Показать причину ошибки
                                        </Typography>
                                        <Typography level="body-xs" sx={{
                                            mt: 0.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere",
                                        }}>
                                            {payment.error_message}
                                        </Typography>
                                    </Box>
                                    {payment.error_code && (
                                        <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.25 }}>
                                            код: {payment.error_code}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        </Sheet>
                    )}

                    {payment.bank_status && <Typography level="body-xs" sx={{ mt: 1, color: 'text.tertiary' }}>
                        Точка: {payment.bank_status}{payment.last_status_check_at ? ` · сверка ${new Date(payment.last_status_check_at).toLocaleString('ru-RU')}` : ''}
                    </Typography>}
                    {payment.status === 'paid' && payment.effects_done === false && <Sheet variant="soft" color="warning" sx={{ mt: 1, p: 1.25, borderRadius: 'md' }}>
                        <Typography level="body-sm">Оплата подтверждена. Учёт или уведомления ещё обрабатываются.</Typography>
                        {payment.effects_error && <Typography level="body-xs" sx={{ overflowWrap: 'anywhere' }}>{payment.effects_error}</Typography>}
                        <Typography level="body-xs">Система повторит незавершённые действия. Новую ссылку создавать не нужно.</Typography>
                    </Sheet>}
                    {payment.can_retry && <Typography level="body-xs" sx={{ mt: 1, color: 'text.tertiary' }}>
                        После исправления причины можно повторить создание с прежним номером заказа. При сетевом сбое сначала проверьте статус.
                    </Typography>}
                    {/* Кнопки */}
                    <Stack direction="row" sx={{ mt: 1.5, flexWrap: "wrap", gap: 0.75 }}>
                        <Button size="sm" variant="soft" color="primary" onClick={() => onOpen(payment)} endDecorator={<ArrowForwardIosRoundedIcon sx={{ fontSize: 11 }} />} sx={{ borderRadius: "lg" }}>
                            Детали
                        </Button>
                        {/* Было условие payment.event_id, а в подписи — application_id:
                            на платеже «от события» без заявки кнопка показывала
                            «К заявке №undefined». */}
                        {payment.application_id && (
                            <Button size="sm" variant="soft" color="success" onClick={() => navigate("/applications")} sx={{ borderRadius: "lg" }}>
                                К заявке №{payment.application_id}
                            </Button>
                        )}
                        {/* can_sync приходит с сервера: если операции в Точке нет и
                            искать нечего, кнопку не показываем — раньше она молча
                            ничего не делала. */}
                        {payment.can_sync !== false && (
                            <Button size="sm" variant="soft" color="neutral" loading={busy === `sync-${payment.id}`} onClick={() => onSync(payment)} startDecorator={<SyncRoundedIcon sx={{ fontSize: 15 }} />} sx={{ borderRadius: "lg" }}>
                                {payment.status === 'paid' && payment.effects_done === false ? 'Повторить учёт оплаты' : 'Проверить статус'}
                            </Button>
                        )}
                        {payment.can_retry && (
                            <Button size="sm" variant="soft" color="warning" loading={busy === `retry-${payment.id}`} onClick={() => onRetry(payment)} startDecorator={<ReplayRoundedIcon sx={{ fontSize: 15 }} />} sx={{ borderRadius: "lg" }}>
                                Повторить создание ссылки
                            </Button>
                        )}
                        {payment.status !== "paid" && payment.status !== "refunded" && (
                            <Button size="sm" variant="solid" color="success" loading={busy === `approve-${payment.id}`} onClick={() => onApprove(payment)} startDecorator={<CheckCircleRoundedIcon sx={{ fontSize: 15 }} />} sx={{ borderRadius: "lg" }}>
                                Одобрить
                            </Button>
                        )}
                        <Button size="sm" variant="soft" color={payment.is_archived ? "primary" : "neutral"} loading={busy === `arch-${payment.id}`}
                            onClick={() => onArchive(payment)}
                            startDecorator={payment.is_archived ? <UnarchiveRoundedIcon sx={{ fontSize: 15 }} /> : <InventoryRoundedIcon sx={{ fontSize: 15 }} />}
                            sx={{ borderRadius: "lg" }}>
                            {payment.is_archived ? "Из архива" : "В архив"}
                        </Button>
                    </Stack>
                </Box>
            </Sheet>
        </motion.div>
    );
}

// ── Строка списка (компактный вид) ───────────────────────
function PaymentRow({ payment, onOpen }) {
    const cfg = PAY_STATUS[payment.status] || PAY_STATUS.created;
    return (
        <Sheet variant="outlined" onClick={() => onOpen(payment)}
            sx={{ borderRadius: "lg", px: 1.5, py: 1, cursor: "pointer", display: "flex", alignItems: "center", gap: 1.5,
                bgcolor: payment.is_archived ? "neutral.100" : "background.surface", "&:hover": { borderColor: "primary.300" } }}>
            <Box sx={{ width: 4, height: 28, borderRadius: 2, bgcolor: cfg.accent }} />
            <Typography level="body-sm" sx={{ fontWeight: 700, minWidth: 44 }}>#{payment.id}</Typography>
            <Chip size="sm" variant="soft" sx={{ color: cfg.accent, bgcolor: cfg.bgSoft }}>{payment.status_display}</Chip>
            <Typography level="body-sm" sx={{ minWidth: 0, flex: 1 }} noWrap>{payment.client_name || payment.address || "—"}</Typography>
            {payment.deal_id && <Typography level="body-xs" sx={{ color: "text.tertiary" }}>Сделка #{payment.deal_id}</Typography>}
            <Typography level="body-sm" sx={{ fontWeight: 800, color: cfg.accent, minWidth: 90, textAlign: "right" }}>{fmtMoney(payment.amount)}</Typography>
        </Sheet>
    );
}

// ── Главная страница ─────────────────────────────────────
export default function ClientPaymentsPage() {
    const { tokenEmployee, notify } = useContext(LayoutContext);
    const navigate = useNavigate();

    const [dateFrom, setDateFrom] = useState(daysAgo(29));
    const [dateTo, setDateTo] = useState(todayISO());

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [showArchived, setShowArchived] = useState(false);
    const [viewMode, setViewMode] = useState("grid");

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);

    const [detail, setDetail] = useState(null);

    const applyPreset = (p) => { setDateFrom(p.from()); setDateTo(p.to()); };

    // Загрузка списка (page=1 — заменяем, иначе докидываем)
    const loadList = useCallback(async (pageToLoad = 1, replace = true) => {
        if (!tokenEmployee) return;
        setLoading(true);
        try {
            const data = await APIGetClientPayments(tokenEmployee, {
                search: search.trim() || undefined,
                status: statusFilter !== "all" ? statusFilter : undefined,
                archived: showArchived ? 1 : undefined,
                date_from: dateFrom,
                date_to: dateTo,
                page: pageToLoad,
                page_size: 20,
            });
            const results = data?.results || [];
            setItems(prev => (replace ? results : [...prev, ...results]));
            setTotal(data?.total || 0);
            setHasMore(!!data?.has_more);
            setPage(pageToLoad);
        } finally { setLoading(false); }
    }, [tokenEmployee, search, statusFilter, showArchived, dateFrom, dateTo]);

    // Сброс на первую страницу при смене фильтров/периода/поиска
    useEffect(() => {
        const t = setTimeout(() => loadList(1, true), 300); // дебаунс поиска
        return () => clearTimeout(t);
    }, [loadList]);

    const replaceItem = (p) => setItems(prev => prev.map(it => (it.id === p.id ? p : it)));

    const handleSync = async (p) => {
        setBusy(`sync-${p.id}`);
        try {
            const updated = await APISyncClientPayment(p.id, tokenEmployee);
            if (updated?.id) replaceItem(updated);
            // Итог проверки сервер кладёт в sync_result. Раньше кнопка на
            // «пустых» карточках молча не делала ничего — теперь видно, что
            // именно произошло.
            const r = updated?.sync_result;
            if (r) notify?.(r.detail || 'Статус актуален.', r.ok ? 'success' : 'warning');
        } catch (e) {
            console.error(e);
            notify?.('Не удалось проверить статус платежа', 'danger');
        } finally { setBusy(null); }
    };

    const handleRetry = async (p) => {
        if (busy) return;
        setBusy(`retry-${p.id}`);
        const updatePayment = (payment) => {
            if (!payment?.id) return;
            setItems(prev => prev.map(item => item.id === p.id ? payment : item));
            setDetail(prev => prev?.id === p.id ? payment : prev);
        };
        try {
            const res = await APIRetryClientPayment(p.id, tokenEmployee);
            updatePayment(res?.payment);
            if (res?.status === 'ok') {
                notify?.(res?.payment?.is_paid ? 'Банк подтвердил оплату; повторная ссылка не нужна' : 'Платёжная ссылка создана', 'success');
            } else {
                notify?.(res?.detail || res?.error || 'Не удалось создать платёж', 'danger');
            }
        } catch (e) {
            // Сервер возвращает обновлённую карточку и при HTTP 502.
            const data = e?.response?.data;
            updatePayment(data?.payment);
            const message = data?.detail || data?.error;
            notify?.(
                typeof message === 'string' ? message :
                    'Не удалось получить результат. Проверьте статус операции перед повтором.',
                'danger'
            );
        } finally {
            setBusy(null);
        }
    };

    const handleArchive = async (p) => {
        setBusy(`arch-${p.id}`);
        try {
            await APIArchiveClientPayment(p.id, !p.is_archived, tokenEmployee);
            // Если включён показ только активных и платёж заархивировали — убираем его из списка
            if (!showArchived && !p.is_archived) {
                setItems(prev => prev.filter(it => it.id !== p.id));
                setTotal(t => Math.max(0, t - 1));
            } else if (showArchived && p.is_archived) {
                setItems(prev => prev.filter(it => it.id !== p.id));
                setTotal(t => Math.max(0, t - 1));
            } else {
                replaceItem({ ...p, is_archived: !p.is_archived });
            }
        } catch (e) {
            console.error(e);
            notify?.('Не удалось изменить архив', 'danger');
        } finally { setBusy(null); }
    };

    const handleApprove = async (p) => {
        // Действие необратимо и стирает причину отказа — спрашиваем.
        if (p.status === 'failed' || p.error_message) {
            const ok = window.confirm(
                'Платёж не прошёл в банке. Отметить его оплаченным вручную?\n\n' +
                'Причина отказа после этого останется только в журнале действий.'
            );
            if (!ok) return;
        }
        setBusy(`approve-${p.id}`);
        try {
            const updated = await APIMarkPaidClientPayment(p.id, tokenEmployee);
            if (updated?.id) replaceItem(updated);
            notify?.('Платёж отмечен оплаченным', 'success');
        } catch (e) {
            console.error(e);
            notify?.('Не удалось подтвердить оплату', 'danger');
        } finally { setBusy(null); }
    };

    return (
        <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, minWidth: 0 }}>
            {/* Заголовок */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: "13px", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", boxShadow: "0 4px 14px rgba(99,102,241,0.3)" }}>
                    <PaymentsRoundedIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, letterSpacing: "-0.5px" }}>Платежи клиентов</Typography>
                    <Typography level="body-xs" sx={{ color: "text.secondary" }}>Эквайринг и статистика по оплатам</Typography>
                </Box>
            </Stack>

            {/* Панель статистики */}
            <PaymentStatsPanel token={tokenEmployee} dateFrom={dateFrom} dateTo={dateTo}
                onPreset={applyPreset} setDateFrom={setDateFrom} setDateTo={setDateTo} />

            {/* Список платежей */}
            <Sheet sx={{ borderRadius: "xl", border: "1px solid", borderColor: "divider", overflow: "hidden", bgcolor: "background.surface" }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between"
                    sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.level1", flexWrap: "wrap", gap: 1 }}>
                    <Box>
                        <Typography level="title-lg" sx={{ fontWeight: 700, lineHeight: 1.1 }}>Платежи</Typography>
                        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>Всего: {total}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Sheet variant="outlined" sx={{ display: "flex", borderRadius: "lg", overflow: "hidden", p: 0.375, gap: 0.375, bgcolor: "background.level1" }}>
                            <Tooltip title="Карточки" variant="soft">
                                <IconButton size="sm" variant={viewMode === "grid" ? "solid" : "plain"} color={viewMode === "grid" ? "primary" : "neutral"} onClick={() => setViewMode("grid")} sx={{ borderRadius: "md", minWidth: 32, minHeight: 32 }}>
                                    <GridViewRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Список" variant="soft">
                                <IconButton size="sm" variant={viewMode === "list" ? "solid" : "plain"} color={viewMode === "list" ? "primary" : "neutral"} onClick={() => setViewMode("list")} sx={{ borderRadius: "md", minWidth: 32, minHeight: 32 }}>
                                    <ViewListRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        </Sheet>
                        <Button onClick={() => loadList(1, true)} startDecorator={<RefreshRoundedIcon />} color="neutral" variant="soft" size="sm" loading={loading} sx={{ borderRadius: "lg" }}>Обновить</Button>
                    </Stack>
                </Stack>

                {/* Фильтры */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ px: 2.5, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Input placeholder="Поиск: телефон, адрес, имя, # платежа/заявки/сделки" value={search} onChange={(e) => setSearch(e.target.value)}
                        startDecorator={<SearchRoundedIcon />} sx={{ flex: 2, borderRadius: "lg" }} />
                    <Select value={statusFilter} onChange={(_, v) => setStatusFilter(v)} startDecorator={<PaymentsRoundedIcon sx={{ fontSize: 18 }} />}
                        sx={{ flex: 1, borderRadius: "lg" }} slotProps={{ listbox: { sx: { zIndex: 100015 } } }}>
                        {STATUS_FILTER_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                    </Select>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography level="body-sm" sx={{ color: "text.secondary" }}>Архив</Typography>
                        <Switch checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
                    </Stack>
                </Stack>

                {/* Контент */}
                <Box sx={{ p: { xs: 1.5, md: 2.5 }, minHeight: 240, position: "relative" }}>
                    {loading && items.length === 0 && (
                        <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}><CircularProgress size="lg" color="primary" /></Stack>
                    )}
                    {!loading && items.length === 0 && (
                        <Stack alignItems="center" spacing={1.5} sx={{ py: 6, color: "text.tertiary" }}>
                            <PaymentsRoundedIcon sx={{ fontSize: 48, opacity: 0.4 }} />
                            <Typography level="title-md">{showArchived ? "Архивных платежей нет" : "Платежей не найдено"}</Typography>
                        </Stack>
                    )}

                    {items.length > 0 && viewMode === "grid" && (
                        <Grid container spacing={2} sx={{ alignItems: "start" }}>
                            <AnimatePresence mode="popLayout">
                                {items.map(p => (
                                    <Grid key={p.id} xs={12} sm={6} lg={4}>
                                        <PaymentCard payment={p} onOpen={setDetail} onArchive={handleArchive} onSync={handleSync} onApprove={handleApprove} onRetry={handleRetry} navigate={navigate} busy={busy} />
                                    </Grid>
                                ))}
                            </AnimatePresence>
                        </Grid>
                    )}
                    {items.length > 0 && viewMode === "list" && (
                        <Stack spacing={0.75}>
                            <AnimatePresence mode="popLayout">
                                {items.map(p => <PaymentRow key={p.id} payment={p} onOpen={setDetail} />)}
                            </AnimatePresence>
                        </Stack>
                    )}

                    {hasMore && (
                        <Stack alignItems="center" sx={{ mt: 2 }}>
                            <Button variant="soft" color="primary" loading={loading} onClick={() => loadList(page + 1, false)} sx={{ borderRadius: "lg" }}>
                                Показать ещё
                            </Button>
                        </Stack>
                    )}
                </Box>
            </Sheet>

            <PaymentDetailModal open={!!detail} payment={detail} onClose={() => setDetail(null)} />
        </Box>
    );
}